import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ArticleIcon from '@mui/icons-material/Article';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import VerifiedIcon from '@mui/icons-material/Verified';
import PageContainer from '../components/shared/pageContainer';
import PageHeader from '../components/shared/page-header';
import { DetailSkeleton } from '../components/shared/skeletons';
import { usePurchaseContractDetailController } from './context/purchase-contract-detail-controller';
import { usePurchaseContractDetailState } from './context/purchase-contract-detail-context';
import PortalAppService from '../../application/platform/portal-app-service';
import { IPurchaseContractFileResponse } from '../../model/base/response/purchase-contract-file-response';

export function PurchaseContractDetailComponent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { loadDetail } = usePurchaseContractDetailController();
  const { contract, loading, error } = usePurchaseContractDetailState();
  const [files, setFiles] = useState<IPurchaseContractFileResponse[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);

  useEffect(() => {
    if (id) {
      loadDetail(parseInt(id));
      loadFiles(parseInt(id));
    }
  }, [id]);

  const loadFiles = async (contractId: number) => {
    setLoadingFiles(true);
    try {
      const response = await PortalAppService.requestPurchaseContractFiles(contractId);
      setFiles(response);
    } catch (error) {
      console.error('Erro ao carregar arquivos:', error);
    } finally {
      setLoadingFiles(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatCurrency = (value?: number) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDocument = (document?: string) => {
    if (!document) return '-';
    if (document.length === 14) {
      return document.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }
    return document;
  };

  const handleViewDocument = (url?: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleDownloadDocument = (url?: string, fileName?: string) => {
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'documento.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (loading) {
    return <DetailSkeleton hasImage={false} hasSections={3} />;
  }

  if (error) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        </Container>
      </PageContainer>
    );
  }

  if (!contract) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <Alert severity="info" sx={{ mt: 2 }}>
            Contrato não encontrado.
          </Alert>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <PageHeader
          title={`Contrato - Processo ${contract.processNumber}`}
          subtitle={contract.contractedCompanyName || 'Detalhes do contrato'}
          breadcrumbs={[
            { label: 'Contratos e Aquisições', href: '/contratos-aquisicoes' },
            { label: contract.processNumber },
          ]}
        />

        {/* Informações Principais */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ArticleIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {contract.processNumber}
              </Typography>
              <Box sx={{ mt: 1 }}>
                {contract.isCovid19 && (
                  <Chip
                    icon={<VerifiedIcon />}
                    label="Contrato COVID-19"
                    color="warning"
                    sx={{ mr: 1 }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            {contract.description && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'start', mb: 2 }}>
                  <DescriptionIcon sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Descrição do Contrato
                    </Typography>
                    <Typography variant="body1">
                      {contract.description}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Empresa Contratada
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {contract.contractedCompanyName || 'Não informado'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    CNPJ: {formatDocument(contract.contractedCompanyDocument)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Prazo do Contrato
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatDate(contract.contractDeadline)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Valores */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <AttachMoneyIcon sx={{ mr: 1 }} />
            Valores Financeiros
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            {contract.contractValue && (
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Valor do Contrato
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="primary.main">
                      {formatCurrency(contract.contractValue)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            <Grid item xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Valor Pago
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="success.main">
                    {formatCurrency(contract.amountPaid)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Saldo Restante
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="warning.main">
                    {formatCurrency(contract.balance)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* Documentos */}
        {files && files.length > 0 && (
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <DescriptionIcon sx={{ mr: 1 }} />
              Documentos ({files.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <List>
              {files.map((doc, index) => (
                <ListItem
                  key={`${doc.sequence}-${index}`}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Visualizar" arrow>
                        <IconButton
                          edge="end"
                          color="primary"
                          onClick={() => handleViewDocument(doc.url)}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'primary.main',
                              color: 'white',
                            },
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Baixar" arrow>
                        <IconButton
                          edge="end"
                          color="success"
                          onClick={() => handleDownloadDocument(doc.url, doc.title)}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'success.main',
                              color: 'white',
                            },
                          }}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                >
                  <ListItemIcon>
                    <DescriptionIcon color="error" sx={{ fontSize: 36 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.title || 'Documento'}
                    secondary={
                      <Box>
                        <Typography variant="caption" display="block">
                          Tipo: {doc.typeDocumentName || 'Não especificado'}
                        </Typography>
                        {doc.publishDate && (
                          <Typography variant="caption" display="block">
                            Publicado em: {formatDate(doc.publishDate)}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {(!files || files.length === 0) && !loadingFiles && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Nenhum documento disponível para este contrato.
          </Alert>
        )}

        {/* Botão de voltar - centralizado no final */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 4,
            mb: 3,
          }}
        >
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/contratos-aquisicoes')}
            size="large"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5,
            }}
          >
            Voltar para Contratos e Aquisições
          </Button>
        </Box>
      </Container>
    </PageContainer>
  );
}
